/**
 * Storefront API base — single resolution for all catalog/checkout fetches.
 *
 * Order (first non-empty wins):
 *   1. `public/runtime-config.js` → window.__ELENARD_RUNTIME_CONFIG__.API_BASE_URL
 *   2. REACT_APP_API_URL (build-time env)
 *   3. Development default: direct Laravel at REACT_APP_DEV_LARAVEL_API_URL or http://127.0.0.1:8000/api/v1
 *      (avoids broken dev-server proxy / SPA 404 on /api/v1). Set REACT_APP_DISABLE_DEV_LARAVEL_DIRECT=1
 *      to skip and use same-origin /api/v1 + src/setupProxy.js only.
 *   4. Production: same-origin `/api/v1/...` (reverse proxy in front of Laravel).
 */

function normalizeBase(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  return s.replace(/\/$/, '');
}

/**
 * If the storefront is served over HTTPS but the API base is still `http://`,
 * browsers block `fetch` (mixed content). Upgrade to `https://` when possible.
 * If your API has no TLS, serve the shop over HTTP or use a same-origin proxy.
 */
function upgradeHttpBaseForHttpsPage(base) {
  if (!base || typeof window === 'undefined') return base;
  try {
    if (window.location.protocol !== 'https:') return base;
    if (!base.startsWith('http://')) return base;
    return `https://${base.slice('http://'.length)}`;
  } catch {
    return base;
  }
}

function getRuntimeApiBase() {
  if (typeof window === 'undefined') return '';
  const cfg = window.__ELENARD_RUNTIME_CONFIG__;
  if (!cfg || typeof cfg.API_BASE_URL !== 'string') return '';
  return normalizeBase(cfg.API_BASE_URL);
}

export function getApiBase() {
  const runtime = getRuntimeApiBase();
  // Explicit runtime URL always wins (deploy-time choice). Ensure backend CORS allows this site if origins differ.
  if (runtime) {
    return upgradeHttpBaseForHttpsPage(runtime);
  }

  const fromEnv = normalizeBase(process.env.REACT_APP_API_URL);
  if (fromEnv) return upgradeHttpBaseForHttpsPage(fromEnv);

  if (
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DISABLE_DEV_LARAVEL_DIRECT !== '1'
  ) {
    return normalizeBase(
      process.env.REACT_APP_DEV_LARAVEL_API_URL || 'https://backend.elenard.com/api/v1',
    );
  }

  // Production default: same-origin `/api/v1` (requires reverse proxy on frontend host).
  return '';
}

export function apiUrl(subPath) {
  const path = subPath.startsWith('/') ? subPath : `/${subPath}`;
  const base = getApiBase();
  if (base) {
    return `${base}${path}`;
  }
  return `/api/v1${path}`;
}

/** Human-readable root used for shop diagnostics. */
export function getResolvedCatalogApiRoot() {
  const base = getApiBase();
  if (base) return base;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1`;
  }
  return '/api/v1';
}
